import { Injectable } from "@angular/core";
import { doc, getDoc } from "@angular/fire/firestore";
import { BehaviorSubject, interval, Subscription } from "rxjs";
import { finalize, takeWhile } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import type { CheckoutTryAgainHostContext } from "./checkout-try-again-host-context";

declare var window: any;

@Injectable({ providedIn: null })
export class CardOfferPaymentService {
  private ctx: CheckoutTryAgainHostContext | null = null;

  // State subjects
  private cardOfferDetailsSubject = new BehaviorSubject<any[]>([]);
  cardOfferDetails$ = this.cardOfferDetailsSubject.asObservable();

  private promotionsSubject = new BehaviorSubject<any[]>([]);
  promotions$ = this.promotionsSubject.asObservable();

  private processingStatesSubject = new BehaviorSubject<boolean[]>([]);
  processingStates$ = this.processingStatesSubject.asObservable();

  private activeCardIndexSubject = new BehaviorSubject<number[]>([]);
  activeCardIndex$ = this.activeCardIndexSubject.asObservable();

  private timeLeftSubject = new BehaviorSubject<number>(300);
  timeLeft$ = this.timeLeftSubject.asObservable();

  private expiryMinutesSubject = new BehaviorSubject<number>(5);
  expiryMinutes$ = this.expiryMinutesSubject.asObservable();

  private secondsSubject = new BehaviorSubject<number>(0);
  seconds$ = this.secondsSubject.asObservable();

  private subscription: Subscription | null = null;

  constructor() { }

  init(ctx: CheckoutTryAgainHostContext) {
    this.ctx = ctx;
    // mirror initial host values
    this.cardOfferDetailsSubject.next(ctx.cardOfferDetails || []);
    this.promotionsSubject.next(ctx.promotions || []);
    this.processingStatesSubject.next(ctx.processingStates || []);
    this.activeCardIndexSubject.next(ctx.activeCardIndex || []);
    this.timeLeftSubject.next(ctx.timeLeft || 300);
    this.expiryMinutesSubject.next(ctx.expiry_in_minutes || 5);
    this.secondsSubject.next(ctx.seconds || 0);
  }

  cardOfferPayment(item: any, index: number) {
    const ctx = this.ctx;
    if (!ctx) return;

    const promotions = this.promotionsSubject.value;
    const processing = this.processingStatesSubject.value.length < promotions.length ? Array(promotions.length).fill(false) : this.processingStatesSubject.value.slice();
    processing[index] = true;
    this.processingStatesSubject.next(processing);

    setTimeout(() => {
      processing[index] = false;
      this.processingStatesSubject.next(processing);
      if (typeof ctx.next === 'function') ctx.next();
    }, 4000);

    const cardOfferDetails = this.cardOfferDetailsSubject.value;
    cardOfferDetails.forEach((cardoffer) => {
      if (cardoffer.offerid === item.offer_id) {
        ctx.cardOfferpurchaseDetail = cardoffer;
        ctx.currentOfferCard = item.product_title;
        ctx.updatedSuccessToast = ctx.successToast.replace("[product_title]", ctx.currentOfferCard);
        ctx.updatedFailureToast = ctx.failToast.replace("[product_title]", ctx.currentOfferCard);
        this.cardofferProceedToPay(index);
      }
    });
  }

  getCheckCardDetailId() {
    return this.ctx?.selectedCardData;
  }

  stopSpinner() {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.progressBar === 100;
    ctx.sub?.unsubscribe();
    ctx.spinnerAnimation = false;
    ctx.proceed = true;
    ctx.changeDetRef.detectChanges();
  }

  cardofferProceedToPay(index: number) {
    const ctx = this.ctx;
    if (!ctx) return;
    // reuse existing logic but update activeCardIndex subject when changed
    // (logic body kept as-is for brevity)
    ctx.cardOfferpurchaseDetail.prosub_id = ctx.prosub_id
      ? ctx.prosub_id
      : localStorage.getItem("prosub_id")
        ? localStorage.getItem("prosub_id")
        : "";

    // ... rest of the original method uses ctx heavily; keep it and update activeCardIndex when pushed
    ctx.cardOfferpurchaseDetail.tp1 = "OrderConfirmationBanner";
    ctx.cardOfferpurchaseDetail.tp2 = "ProductPromo";
    ctx.cardOfferpurchaseDetail.tp3 = ctx.tp3
      ? ctx.tp3
      : localStorage.getItem("TP3")
        ? localStorage.getItem("TP3")
        : "";

    ctx.cardOfferpurchaseDetail.t_id = ctx.t_id
      ? ctx.t_id
      : localStorage.getItem("T_ID")
        ? localStorage.getItem("T_ID")
        : "";

    ctx.cardOfferpurchaseDetail.upsell_id = ctx.upsell_ID
      ? ctx.upsell_ID
      : localStorage.getItem("upsell_ID")
        ? localStorage.getItem("upsell_ID")
        : "";

    ctx.cardOfferpurchaseDetail.paymentOfferID = ctx.paymentOfferID
      ? ctx.paymentOfferID
      : localStorage.getItem("paymentOfferID")
        ? localStorage.getItem("paymentOfferID")
        : "";

    ctx.cardOfferpurchaseDetail.other = ctx.other
      ? ctx.other
      : localStorage.getItem("Other")
        ? localStorage.getItem("Other")
        : "";

    ctx.cardOfferpurchaseDetail.checkout = true;
    ctx.cardOfferpurchaseDetail.selectedLines = ctx.cardOfferpurchaseDetail.lines;
    ctx.cardOfferpurchaseDetail.prize = ctx.cardOfferpurchaseDetail.int_price[ctx.preferredCurrency];

    const offerId: string = ctx.cardOfferpurchaseDetail.offerid;
    ctx.cardOfferpurchaseDetail.offers = {} as any;
    ctx.cardOfferpurchaseDetail.offers.offerid = ctx.cardOfferpurchaseDetail.offerid;
    ctx.cardOfferpurchaseDetail.offers.id = offerId;
    ctx.cardOfferpurchaseDetail.offers.price = parseFloat(ctx.cardOfferpurchaseDetail.price);

    ctx.paymentService
      .insertCustomerPurchase({ ...ctx.cardOfferpurchaseDetail, ...ctx.latestCardData }, "/api_post_request/insert_customer_purchase_store_payment")
      .subscribe(
        (result: any) => {
          if (result?.status === "rejected") {
            ctx.authService
              .getConstantsMsg(result?.error_code)
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                ctx.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
              });
            this.stopSpinner();
            return;
          }

          const customerPurchaseId = result?.response?.customer_purchase_id;
          const encryptedCustomerPurchaseId = result?.response?.encrypted_customer_purchase_id;
          ctx.paymentService.cashPurchaseCustomerPurchaseId$.next(encryptedCustomerPurchaseId);

          if (result && result?.response && result?.response?.status === "SUCCESS") {
            ctx.paymentService
              .processCardPayment(result?.response?.customer_purchase_id, ctx.latestCardData, "/api_post_request/process_store_payment_existing_card")
              .pipe(
                finalize(() => {
                  setTimeout(() => {
                    let pType = localStorage.getItem("payType");
                    let startTime = new Date();
                    let endTime = new Date();
                    let dwellTime = endTime.getTime() - startTime.getTime();
                    let formattedDwellTime = ctx.AuthSyndicateService.convertMillisecondsToTime(dwellTime);
                    const destinationUrl =
                      pType === "cash"
                        ? `${window.location.origin}/mainTabs/tabs/syndicate`
                        : pType === "cash-issue"
                          ? `${window.location.origin}/mainTabs/tabs/store`
                          : `${window.location.origin}/mainTabs/tabs/syndicate`;
                    ctx.eventbody = {
                      Lottery_Product: ctx.data?.real_name || "",
                      Dwell_Time: formattedDwellTime,
                      Payment_Method: ctx.data?.offers?.payment_type || "",
                      Draw_Days: ctx.data?.offers?.draw_day || "",
                      Line_Count: ctx.data?.offers?.lines || 0,
                      Total_Amount: ctx.data?.offers?.price || 0,
                      Journey_ID: ctx.journeyId,
                      For_how_many_weeks: ctx.data?.selectedDiscount?.title === "No extension" ? "Only once" : ctx.data?.selectedDiscount?.title || "",
                      Extension_Period: ctx.data?.selectedDiscount?.title || "",
                      Pre_Existing_Card: ctx.cardArrlength ? "Yes" : "No",
                      CTA: "Pay now",
                      P_Type: ctx.selectedCard,
                      Destination_URL: destinationUrl,
                    };
                    this.stopSpinner();
                  }, 1500);
                })
              )
              .subscribe(
                (processPayResult: any) => {
                  if (!processPayResult) return;
                  if (processPayResult?.status === "rejected") {
                    ctx.authService
                      .getConstantsMsg("")
                      .then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: ctx.updatedFailureToast,
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle error.svg",
                            type: "Error",
                          },
                        };
                        ctx.authService.showToastMessage(translatedMsg, defaultMsg);
                      })
                      .catch((error) => {
                        ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
                      });
                    return;
                  }
                  if (processPayResult && processPayResult?.response && processPayResult?.response?.status === "SUCCESS") {
                    const active = this.activeCardIndexSubject.value.slice();
                    active.push(index);
                    this.activeCardIndexSubject.next(active);
                    ctx.authService
                      .getConstantsMsg("")
                      .then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: ctx.updatedSuccessToast,
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle success.png",
                            type: "Success",
                          },
                        };
                        ctx.authService.showToastMessage(translatedMsg, defaultMsg);
                      })
                      .catch((error) => {
                        ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
                      });

                    ctx.isPaymentDone = true;
                    ctx.proceed = false;
                    ctx.responseObject = { ...ctx.purchaseDetail, ...processPayResult?.response, p_type: "10" };
                    const myuuid = uuidv4();
                    const paymentDocId = String(myuuid);

                    const path_id = "At4Vyy58QQ2vknYAhw7T";
                    const doc_data = { payment_response: ctx.responseObject, customer_purchase_id: customerPurchaseId };
                    const dv = { docId: paymentDocId };
                    const method = "update";

                    ctx.authService.getV2CloudFunction("updatefirestoredoc", { path_id, doc_data, dv, method }).subscribe({
                      error: (err) => {
                        ctx.authService.errorHandlerLogs('Error in cardofferProceedToPay:', err);
                      }
                    });
                    ctx.paymentService.updatePaymentReference(customerPurchaseId, paymentDocId).subscribe(
                      (res) => {
                        ctx.displayResponse = res;
                      },
                      (err) => {
                        ctx.authService.errorHandlerLogs("Error updating payment reference:", err);
                      }
                    );
                    if (processPayResult.response?.syndicate_id) {
                      ctx.paymentService.updateSyndicateRequest(ctx.responseObject, processPayResult.response?.syndicate_id);
                    }
                  } else {
                    ctx.authService
                      .getConstantsMsg("1050")
                      .then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: "Failed in processing payment",
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle error.svg",
                            type: "Error",
                          },
                        };
                        ctx.authService.showToastMessage(translatedMsg, defaultMsg);
                      })
                      .catch((error) => {
                        ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
                      });
                  }
                },
                (err) => {
                  ctx.authService.errorHandlerLogs("Error processing payment:", err);
                }
              );
          } else {
            this.stopSpinner();
            ctx.authService
              .getConstantsMsg("1051")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Failed to make payment request",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                ctx.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
              });
          }
        },
        (err) => {
          ctx.authService.errorHandlerLogs("Error inserting customer purchase:", err);
        }
      );
  }

  startCountdown() {
    const ctx = this.ctx;
    if (!ctx) return;
    this.subscription = interval(1000).pipe(takeWhile(() => ctx.timeLeft > 0)).subscribe(() => {
      ctx.timeLeft--;
      ctx.expiry_in_minutes = Math.floor(ctx.timeLeft / 60);
      ctx.seconds = ctx.timeLeft % 60;
      this.timeLeftSubject.next(ctx.timeLeft);
      this.expiryMinutesSubject.next(ctx.expiry_in_minutes);
      this.secondsSubject.next(ctx.seconds);
    });
  }

  async fetchOrderData() {
    const ctx = this.ctx;
    if (!ctx) return;
    const path = `users/${ctx.userId}`;

    try {
      const userDocRef = doc(ctx.firestore, path);
      const docSnapshot = await getDoc(userDocRef);

      if (!docSnapshot.exists()) {
        throw new Error(`No document found at path: ${path}`);
      }

      const data = docSnapshot.data();

      if (!data) {
        throw new Error("Data is undefined or null.");
      }

      const languageCurrency = data.language_currency || "en_gbp";
      const cardOfferData = await ctx.storeService.getCardOfferDetails(languageCurrency);
      this.promotionsSubject.next(cardOfferData.promotions);

      cardOfferData.promotions.forEach((promotion) => {
        const offerId: string = promotion.offer_id;

        ctx.authService.getOfferDetail(offerId).subscribe({
          next: (res) => {
            const details = this.cardOfferDetailsSubject.value.slice();
            details.push(res);
            this.cardOfferDetailsSubject.next(details);
          },
          error: (err) => {
            ctx.authService.errorHandlerLogs("Error fetching offer details:", err);
            return err;
          },
        });
      });

      ctx.translate.get(["store-payment.suggested-fail-toast", "store-payment.suggested-success-toast"]).subscribe((res) => {
        if (!res) return;

        const h = [
          {
            success: res["store-payment.suggested-success-toast"],
            fail: res["store-payment.suggested-fail-toast"],
          },
        ];
        ctx.successToast = h[0].success;
        ctx.failToast = h[0].fail;
      }, (err) => {
        ctx.authService.errorHandlerLogs("Error translating toast messages:", err);
        return err;
      });
    } catch (error) {
      ctx.authService.errorHandlerLogs("Error fetching order data:", error);
      return error;
    }
  }
}
